import React, { useEffect, useState } from "react";
import {
  fetchTopEntities,
  fetchCategoryEntities,
} from "../../services/entityCloudService";

const STYLES = {
  3: "text-[15px] text-brand px-1 py-0.5",
  2: "text-[12.5px] text-ink bg-chip border border-line px-2 py-1 rounded-pill",
  1: "text-[11.5px] text-chip-ink bg-chip border border-line px-2 py-1 rounded-pill",
  0: "text-[11px] text-sub bg-chip border border-line px-2 py-1 rounded-pill",
};

function formatTime(date) {
  if (!date) return "";
  try {
    const d = new Date(date);
    return d.toLocaleString("sq-AL", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function hostnameFromUrl(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

export default function EntityCloud({
  title = "Top personat & entitetet",
  category,
}) {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSelected(null); // reset selection if the category context changes
    const loader = category
      ? fetchCategoryEntities(category)
      : fetchTopEntities();
    loader.then((list) => {
      if (cancelled) return;
      setEntities(list);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [category]);

  // ─── Selected entity → show its article list ─────────────────────────────
  if (selected) {
    const articles = selected.articles || [];
    return (
      <div className="rounded-card border border-line bg-surface p-5">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-head text-[11.5px] font-bold uppercase tracking-wider text-sub">
              Lajme për
            </h3>
            <p className="mt-0.5 font-head text-[17px] font-bold text-ink">
              {selected.name}
            </p>
            <p className="mt-0.5 text-[11.5px] text-sub">
              {articles.length} artikuj
            </p>
          </div>
          <button
            type="button"
            onClick={() => setSelected(null)}
            title="Mbyll"
            className="grid h-7 w-7 shrink-0 place-items-center rounded-pill bg-chip text-[14px] text-chip-ink hover:bg-ai-bg hover:text-ai"
          >
            ×
          </button>
        </div>

        {articles.length === 0 ? (
          <p className="text-[12.5px] text-sub">Asnjë artikull i lidhur.</p>
        ) : (
          <ul className="max-h-[60vh] overflow-y-auto pr-1">
            {articles.map((a, i) => (
              <li
                key={`${a.link || a.title}-${i}`}
                className="border-t border-line py-2.5 first:border-t-0"
              >
                <a
                  href={a.link || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:underline"
                >
                  <p className="line-clamp-2 text-[12.5px] font-semibold leading-snug text-ink">
                    {a.title}
                  </p>
                </a>
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10.5px] text-sub">
                  <span className="font-head font-semibold text-brand">
                    {hostnameFromUrl(a.link)}
                  </span>
                  {a.published_date && (
                    <span>· {formatTime(a.published_date)}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  // ─── Default: the weighted entity cloud ──────────────────────────────────
  return (
    <div className="rounded-card border border-line bg-surface p-5">
      <h3 className="mb-3.5 font-head text-[12px] font-bold uppercase tracking-wider text-sub">
        {title}
      </h3>
      {loading ? (
        <div className="text-[12.5px] text-sub">Po ngarkohet…</div>
      ) : entities.length === 0 ? (
        <div className="text-[12.5px] text-sub">Nuk ka entitete.</div>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5">
          {entities.map((e) => (
            <button
              key={e.name}
              type="button"
              onClick={() => setSelected(e)}
              title={`${e.count || 0} artikuj`}
              className={`inline-block whitespace-nowrap font-head font-semibold leading-tight transition hover:text-brand ${STYLES[e.weight]}`}
            >
              {e.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
